/**
 * Service to handle automatic user logoff after a configurable period of inactivity.
 * The timeout is configured via the nodeAutoLogoff field in MMamaSettings.
 */
export class AutoLogoffService {
  private static instance: AutoLogoffService;
  private static readonly WARNING_TIME_SECONDS = 30; // Warning shown 30 seconds before logout
  private logoffTimeoutId: NodeJS.Timeout | null = null;
  private warningTimeoutId: NodeJS.Timeout | null = null;
  private autoLogoffMinutes = 0;
  private onLogout: (() => void) | null = null;
  private onWarning: ((minutesRemaining: number) => void) | null = null;
  private activityEvents = ['mousedown', 'keypress', 'scroll', 'touchstart', 'click'];

  private constructor() {}

  public static getInstance(): AutoLogoffService {
    if (!AutoLogoffService.instance) {
      AutoLogoffService.instance = new AutoLogoffService();
    }
    return AutoLogoffService.instance;
  }

  /**
   * Initialize the auto-logoff service with timeout in minutes
   * @param autoLogoffMinutes - The timeout in minutes (from settings)
   * @param onLogout - Callback function to execute when auto-logout occurs
   * @param onWarning - Optional callback function to execute when warning is shown
   */
  public initializeAutoLogoff(
    autoLogoffMinutes: number,
    onLogout: () => void,
    onWarning?: (minutesRemaining: number) => void
  ): void {
    this.onLogout = onLogout;
    this.onWarning = onWarning;
    this.autoLogoffMinutes = autoLogoffMinutes || 0;

    if (this.autoLogoffMinutes > 0) {
      this.setupActivityListeners();
      this.resetTimeout();
    }
  }

  /**
   * Set up event listeners for user activity detection
   */
  private setupActivityListeners(): void {
    this.activityEvents.forEach(event => {
      document.addEventListener(event, this.resetTimeout, true);
    });
  }

  /**
   * Remove activity event listeners
   */
  private removeActivityListeners(): void {
    this.activityEvents.forEach(event => {
      document.removeEventListener(event, this.resetTimeout, true);
    });
  }

  /**
   * Reset the timeout when user activity is detected
   */
  private resetTimeout = (): void => {
    if (this.autoLogoffMinutes <= 0) return;

    // Clear existing timeouts
    if (this.logoffTimeoutId) {
      clearTimeout(this.logoffTimeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }

    const logoffTimeMs = this.autoLogoffMinutes * 60 * 1000;
    const warningTimeMs = AutoLogoffService.WARNING_TIME_SECONDS * 1000;

    // Set warning timeout
    this.warningTimeoutId = setTimeout(() => {
      // Calculate remaining time in seconds, then convert to minutes for the component
      const remainingSeconds = AutoLogoffService.WARNING_TIME_SECONDS;
      const remainingMinutes = remainingSeconds / 60; // Convert seconds to minutes
      if (this.onWarning) {
        this.onWarning(remainingMinutes);
      }
    }, logoffTimeMs - warningTimeMs);

    // Set logoff timeout
    this.logoffTimeoutId = setTimeout(() => {
      if (this.onLogout) {
        this.onLogout();
      }
    }, logoffTimeMs);
  };

  /**
   * Clean up the service and remove all listeners and timeouts
   */
  public destroy(): void {
    this.removeActivityListeners();
    if (this.logoffTimeoutId) {
      clearTimeout(this.logoffTimeoutId);
    }
    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
    }
    this.autoLogoffMinutes = 0;
    this.onLogout = null;
    this.onWarning = null;
  }

  /**
   * Manually extend the session by resetting the timeout
   */
  public extendSession(): void {
    this.resetTimeout();
  }

  /**
   * Check if auto-logoff is currently active
   */
  public isActive(): boolean {
    return this.autoLogoffMinutes > 0;
  }

  /**
   * Get the configured auto-logoff time in minutes
   */
  public getAutoLogoffMinutes(): number {
    return this.autoLogoffMinutes;
  }
}
